import React from "react";
import { Link } from "react-router-dom";
import { StatusPill, TableState } from "../admin/components/panels";
import { Skeleton } from "../admin/components/primitives";
import { clean, count, money, prettyDate, toNumber } from "../admin/format";

export const VOUCHER_TYPES = {
  receipt: "Receipt",
  payment: "Payment",
  contra: "Contra",
  journal: "Journal",
};

/** Money that reads red when negative, so a loss or overdraft is never missed. */
export const Signed = ({ value }) => <span className={toNumber(value) < 0 ? "dash-neg" : undefined}>{money(value)}</span>;

const Change = ({ value, opening }) => {
  const diff = toNumber(value) - toNumber(opening);
  if (Math.abs(diff) < 0.5) return <span className="dash-muted">No change</span>;
  return (
    <span className="dash-muted">
      {diff > 0 ? "+" : "−"}
      {money(Math.abs(diff))}
    </span>
  );
};

/* ------------------------------------------------------------------ lists */

/** Label / amount rows; amounts can be negative. `strong` rows are subtotals. */
export const LedgerList = ({ rows, loading }) => {
  if (loading) return <Skeleton height={160} />;
  return (
    <ul className="dash-minilist dash-ledger">
      {rows.map((row) => (
        <li key={row.name} className={row.strong ? "is-total" : undefined}>
          <span>
            {row.name}
            {row.note && <span className="dash-muted tx-11"> · {row.note}</span>}
          </span>
          <strong>
            <Signed value={row.value} />
          </strong>
        </li>
      ))}
    </ul>
  );
};

/* ---------------------------------------------------------------- tables */

/** Working-capital lines at the end of the period against its opening. */
export const PositionTable = ({ rows, openingAsOf, asOf, loading }) => (
  <TableState loading={loading} empty={rows.length === 0}>
    <table className="dash-table">
      <caption className="visually-hidden">Working capital balances at the start and end of the period</caption>
      <thead>
        <tr>
          <th scope="col">Account</th>
          <th scope="col" className="num">
            {openingAsOf ? prettyDate(openingAsOf) : "Opening"}
          </th>
          <th scope="col" className="num">
            {prettyDate(asOf)}
          </th>
          <th scope="col" className="num">
            Change
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.key}>
            <td>
              {row.label}
              {row.name && row.name !== row.label && <div className="dash-muted tx-11">{row.name}</div>}
            </td>
            <td className="num">
              <Signed value={row.opening} />
            </td>
            <td className="num">
              <Signed value={row.value} />
            </td>
            <td className="num">
              <Change value={row.value} opening={row.opening} />
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  </TableState>
);

export const SupplierPayableTable = ({ rows, asOf, loading }) => (
  <TableState loading={loading} empty={rows.length === 0}>
    <table className="dash-table">
      <caption className="visually-hidden">Suppliers with the largest amounts owed</caption>
      <thead>
        <tr>
          <th scope="col">Supplier</th>
          <th scope="col">Last entry</th>
          <th scope="col" className="num">
            Payable
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row, index) => (
          <tr key={row.id}>
            <td>
              <span className="dash-rank" aria-hidden="true">
                {index + 1}
              </span>
              <Link to={`/supplier-payable?as_of=${asOf}`}>{clean(row.name)}</Link>
              {row.code && <div className="dash-muted tx-11">{row.code}</div>}
            </td>
            <td>{prettyDate(row.last_date)}</td>
            <td className="num">{money(row.balance)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </TableState>
);

export const VoucherTypeTable = ({ rows, loading }) => (
  <TableState loading={loading} empty={rows.every((row) => toNumber(row.vouchers) === 0)}>
    <table className="dash-table">
      <caption className="visually-hidden">Vouchers posted in the period by type</caption>
      <thead>
        <tr>
          <th scope="col">Type</th>
          <th scope="col" className="num">
            Vouchers
          </th>
          <th scope="col" className="num">
            Value
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.type}>
            <td>{VOUCHER_TYPES[row.type] || row.type}</td>
            <td className="num">{count(row.vouchers)}</td>
            <td className="num">{money(row.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </TableState>
);

export const RecentVouchersTable = ({ rows, loading }) => (
  <TableState loading={loading} empty={rows.length === 0}>
    <table className="dash-table">
      <caption className="visually-hidden">Most recently dated journal vouchers</caption>
      <thead>
        <tr>
          <th scope="col">Voucher</th>
          <th scope="col">Type</th>
          <th scope="col" className="num">
            Amount
          </th>
        </tr>
      </thead>
      <tbody>
        {rows.map((row) => (
          <tr key={row.id}>
            <td>
              <Link to={`/journal-voucher-details/${row.id}`}>{row.serial_number}</Link>
              <div className="dash-muted tx-11">
                {prettyDate(row.date)}
                {row.journal_code && ` · ${clean(row.journal_code)}`}
              </div>
            </td>
            <td>
              <StatusPill tone="mute">{VOUCHER_TYPES[row.type] || row.type}</StatusPill>
            </td>
            <td className="num">{money(row.total_amount)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  </TableState>
);

/* ----------------------------------------------------------------- checks */

/**
 * Book-keeping health. Every problem names what is wrong and where to look;
 * status is shown with an icon and words, never colour alone.
 */
export const BooksCheck = ({ checks, asOf, range, loading }) => {
  if (loading) return <Skeleton height={120} />;

  const items = [
    {
      key: "balance_sheet",
      ok: !checks.issues.includes("balance_sheet"),
      title: "Balance sheet balances",
      problem: `Assets differ from equity and liabilities by ${money(checks.balanceSheetDifference)} as at ${prettyDate(asOf)}.`,
      to: `/statement-of-financial-position?as_of=${asOf}`,
      link: "Open balance sheet",
    },
    {
      key: "unbalanced",
      ok: !checks.issues.includes("unbalanced"),
      title: "Every voucher balances",
      problem: `${count(checks.unbalancedCount)} voucher(s) have debits that do not equal credits.`,
      to: "/journal-voucher",
      link: "Journal vouchers",
    },
    {
      key: "cash",
      ok: !checks.issues.includes("cash"),
      title: "Cash movements reconcile",
      problem: `Opening cash plus net cash flow is off from closing cash by ${money(checks.cashDifference)}.`,
      to: range.from ? `/cash-flow-statement?from=${range.from}&to=${range.to}` : "/cash-flow-statement",
      link: "Open cash flow",
    },
  ];

  return (
    <>
      <ul className="dash-checks">
        {items.map((item) => (
          <li key={item.key} className={item.ok ? "is-ok" : "is-off"}>
            <i className={`fa ${item.ok ? "fa-check-circle" : "fa-exclamation-triangle"}`} aria-hidden="true" />
            <div>
              <strong>
                {item.title}
                <span className="visually-hidden">{item.ok ? ": passed" : ": needs attention"}</span>
              </strong>
              {!item.ok && (
                <p>
                  {item.problem} <Link to={item.to}>{item.link}</Link>
                </p>
              )}
            </div>
          </li>
        ))}
      </ul>
      {checks.unbalanced.length > 0 && (
        <ul className="dash-minilist mg-t-10">
          {checks.unbalanced.map((row) => (
            <li key={row.id}>
              <span>
                <Link to={`/journal-voucher-details/${row.id}`}>{row.serial_number}</Link>
                <span className="dash-muted tx-11"> · {prettyDate(row.date)}</span>
              </span>
              <strong>
                <Signed value={row.difference} />
              </strong>
            </li>
          ))}
        </ul>
      )}
    </>
  );
};

/* ---------------------------------------------------------------- reports */

/** Shortcuts into the accounting reports, opened on the dashboard's period. */
export const reportLinks = (range) => {
  const period = range.from ? `from=${range.from}&to=${range.to}` : `to=${range.to}`;
  return [
    { to: `/profit-and-loss?${period}`, title: "Profit & Loss", icon: "fa-area-chart" },
    { to: `/cash-flow-statement?${period}`, title: "Cash Flow Statement", icon: "fa-exchange" },
    { to: `/statement-of-financial-position?as_of=${range.to}`, title: "Balance Sheet", icon: "fa-university" },
    { to: `/trial-balance?${period}`, title: "Trial Balance", icon: "fa-balance-scale" },
    { to: `/receipts-payments-voucher-summary?${period}`, title: "Receipts & Payments", icon: "fa-list-alt" },
    { to: "/chart-of-account-ledger-report", title: "Account Ledger", icon: "fa-book" },
    { to: `/supplier-payable?as_of=${range.to}`, title: "Supplier Payable", icon: "fa-credit-card" },
    { to: `/supplier-advance?as_of=${range.to}`, title: "Supplier Advance", icon: "fa-forward" },
    { to: `/ledger-wise-transactions-present-balance?as_of=${range.to}`, title: "Supplier Ledger Balances", icon: "fa-book" },
    { to: `/distributor-receivable-report?as_of=${range.to}`, title: "Distributor Receivable", icon: "fa-arrow-circle-down" },
  ];
};

export const ReportLinks = ({ links }) => (
  <nav className="dash-links" aria-label="Accounting reports">
    {links.map((link) => (
      <Link key={link.title} className="dash-link" to={link.to}>
        <i className={`fa ${link.icon}`} aria-hidden="true" />
        <span>{link.title}</span>
      </Link>
    ))}
  </nav>
);
